import { ConditionChild, ConditionGroup, DesignData, MediaElement } from 'editor/src/store/design/types';

import { GEOMETRY_KEYS, isUsableNumber } from './dropNonFiniteGeometry';

// A geometry key that is present but not a usable number (saved as `null`
// from a NaN) means the element was never drawn on the canvas, and Preflight
// rejects every render of the design. Keys that are absent are left alone.
export const hasUnplaceableGeometry = (element: MediaElement) => {
  const record = element as unknown as Record<string, unknown>;
  return GEOMETRY_KEYS.some((key) => record[key] !== undefined && !isUsableNumber(record[key]));
};

// The same buckets removeMediaElement prunes, plus the personalization field
// groups, so no reference to a removed element survives into the next save.
function pruneConditionGroup(conditionGroup: ConditionGroup, removedUuids: Set<number>): ConditionGroup {
  const keep = (child: ConditionChild) => child.type === 'condition' || !removedUuids.has(child.id);
  const children = Object.fromEntries(
    Object.entries(conditionGroup.children).map(([key, bucket]) => [key, bucket.filter(keep)]),
  );
  const fieldGroups = conditionGroup.fieldGroups?.map((group) => ({ ...group, elements: group.elements.filter(keep) }));
  return { ...conditionGroup, children, ...(fieldGroups ? { fieldGroups } : {}) };
}

// Returns the same design when nothing needs removing.
function removeUnplaceableElements(designData: DesignData): { designData: DesignData; removed: MediaElement[] } {
  const removed: MediaElement[] = [];
  const spreads = designData.spreads.map((spread) => {
    const removedInSpread: MediaElement[] = [];
    const pages = spread.pages.map((page) => {
      const { media } = page.groups;
      if (!media?.some(hasUnplaceableGeometry)) {
        return page;
      }
      removedInSpread.push(...media.filter(hasUnplaceableGeometry));
      return {
        ...page,
        groups: { ...page.groups, media: media.filter((element) => !hasUnplaceableGeometry(element)) },
      };
    });
    if (!removedInSpread.length) {
      return spread;
    }

    removed.push(...removedInSpread);
    const removedUuids = new Set(
      removedInSpread.map((element) => element.uuid).filter((uuid): uuid is number => uuid !== undefined),
    );
    return {
      ...spread,
      pages,
      ...(spread.conditionGroup && removedUuids.size
        ? { conditionGroup: pruneConditionGroup(spread.conditionGroup, removedUuids) }
        : {}),
    };
  });

  return removed.length ? { designData: { ...designData, spreads }, removed } : { designData, removed };
}

export default removeUnplaceableElements;
